import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import { batch, useSelector } from 'react-redux';
import {
  TChildrenPayload,
  TConfigurationManagementCentralProps,
} from 'src/components/ConfigurationManagementCentral';
import { THandleAssetValuesParameters } from 'src/components/ConfigurationManagementCentral/ConfigurationManagementCentral.types';
import { prepareSettingsInput } from 'src/components/ConfigurationManagementCentral/prepareSettingsInput';
import { TAssetsSaveProps } from 'src/components/FormAssetsParams';
import { EModalAssetsManagerView } from 'src/components/ModalAssetsManager';
import { GRID_MARKET_ASSET_TYPES } from 'src/constants/application';
import { EPredefinedModalIds } from 'src/constants/modals';
import {
  AreaInput,
  ReadConfigurationDocument,
  useCreateConfigurationAreaMutation,
  useRemoveConfigurationAreaMutation,
  useSetGlobalSimulationLocationMutation,
  useUpdateConfigurationAreaMutation,
  useUpdateConfigurationMutation,
  useUpdateProjectMutation,
} from 'src/graphql';
import useBuildSavingTreeBranch from 'src/hooks/useBuildSavingTreeBranch';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import { useMapNavigation } from 'src/hooks/useMapNavigation';
import { useMemberEvents } from 'src/hooks/useMemberEvents';
import { useSyncConfigurationWithBE } from 'src/hooks/useSyncConfigurationForEvents';
import {
  selectIsCustomHouseAddingContinuation,
  selectModalHouseSummaryView,
} from 'src/redux/application/application.selectors';
import {
  initialApplicationState,
  setIsCustomHouseAddingContinuation,
  setModalCommunitySummaryView,
} from 'src/redux/application/application.slice';
import { selectIsLoggedIn } from 'src/redux/auth/auth.selectors';
import { cacheUpdate } from 'src/redux/cacheUpdate';
import {
  selectActiveConfigurationUuid,
  selectAssets,
  selectAssetsAmountBelowCommunity,
  selectAssetsTreeRelations,
  selectAssetsValues,
  selectCommunityAsset,
  selectConfigType,
  selectConfigurationCenter,
  selectConfiguration,
  selectIsCommunityCreatedInDB,
  selectRootAssetUuid,
  selectSettingsData,
  selectSimulationStatus,
} from 'src/redux/configuration/configuration.selectors';
import {
  addAssets,
  appendBranch,
  removeAssetByUuid,
  removeAssetByUuidWithDelete,
  resetSimulationResults,
  setAssetLoadingInfo,
  setLastAddedAssetUuid,
  setRootAsset,
  setSelectedAssetUuid,
  updateAssetTypeForUuid,
  updateAssetValuesForUuid,
  setCommunityLogo,
} from 'src/redux/configuration/configuration.slice';
import { selectSelectedLocation } from 'src/redux/map/map.selectors';
import { updateSelectedLocation } from 'src/redux/map/map.slice';
import { openModal } from 'src/redux/modals/modals.slice';
import { selectSCMFlow, selectCommunityMembers } from 'src/redux/scm/scm.selectors';
import { useAppDispatch } from 'src/redux/store';
import { openToast } from 'src/redux/toast/toast.slice';
import { TAssetType } from 'src/typings/base-types';
import { TAsset, TAssetArea, TConfigTree, TTreeBranch } from 'src/typings/configuration.types';
import {
  getAllFieldTemplatesWithValuesForAsset,
  getAssetValues,
} from 'src/utils/assetsFields/fieldTemplatesWithValues';
import { TDeviceTypesInput, prepareAreaInput } from 'src/utils/mutationsGates';
import { v4 } from 'uuid';

export function ConfigurationManagementCentral({
  children,
}: TConfigurationManagementCentralProps): ReturnType<
  TConfigurationManagementCentralProps['children']
> {
  const dispatch = useAppDispatch();
  const isSCMFlow = useSelector(selectSCMFlow);
  const scmMembers = useSelector(selectCommunityMembers);
  const assets = useSelector(selectAssets);
  const communityAsset = useSelector(selectCommunityAsset);
  const isConfigEmpty = Object.keys(assets).length === 0;
  const assetsTreeRelations = useSelector(selectAssetsTreeRelations);
  const assetsAmountBelowCommunity = useSelector(selectAssetsAmountBelowCommunity);
  const { syncConfigurationWithBE } = useSyncConfigurationWithBE();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const settingsData = useSelector(selectSettingsData);
  const configurationCharacteristic = useSelector(selectConfiguration);
  const configType = useSelector(selectConfigType);
  const rootAssetUuid = useSelector(selectRootAssetUuid);
  const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
  const assetsValues = useSelector(selectAssetsValues);
  const isCommunityCreatedInDB = useSelector(selectIsCommunityCreatedInDB);
  const configurationCenter = useSelector(selectConfigurationCenter);
  const isCustomHouseAddingContinuation = useSelector(selectIsCustomHouseAddingContinuation);
  const modalHouseSummaryView = useSelector(selectModalHouseSummaryView);
  const selectedLocation = useSelector(selectSelectedLocation);
  const { zoomIntoConfiguration } = useMapNavigation();
  const [doSyncWithBackend, setDoSyncWithBackend] = useState<boolean>(false);
  const [doZoomIntoConfiguration, setDoZoomIntoConfiguration] = useState<boolean>(false);
  const saveKeyRef = useRef<string | null>(null);
  const { handleReadConfiguration } = useMemberEvents({});
  // Mutations
  const [updateConfiguration] = useUpdateConfigurationMutation({
    errorPolicy: 'all',
    refetchQueries: [
      { query: ReadConfigurationDocument, variables: { uuid: activeConfigurationUuid } },
    ],
  });
  const [updateProject] = useUpdateProjectMutation({ errorPolicy: 'all' });
  const [createConfigurationArea] = useCreateConfigurationAreaMutation({
    errorPolicy: 'all',
  });
  const [removeConfigurationArea] = useRemoveConfigurationAreaMutation({
    onError: (err) => {
      const msg = JSON.parse(err.message).remove_configuration_area;
      dispatch(
        openToast({
          message: msg || 'Something went wrong',
          type: 'error',
        }),
      );
    },
  });
  const [updateConfigurationArea] = useUpdateConfigurationAreaMutation({
    errorPolicy: 'all',
  });

  const [setGlobalSimulationLocation] = useSetGlobalSimulationLocationMutation({
    errorPolicy: 'all',
  });
  const {
    createAreaInputFromSavingTreeBranch,
    createSavingTreeBranch,
    createAreaInputFromSaveProps,
  } = useBuildSavingTreeBranch();

  const isCN = configType === 'CANARY_NETWORK';
  const centreCoordinates = useMemo(() => {
    return {
      longitude: configurationCenter?.lng,
      latitude: configurationCenter?.lat,
    };
  }, [configurationCenter?.lat, configurationCenter?.lng]);

  const handleUpdateCommunity = useCallback(async () => {
    if (!activeConfigurationUuid || !configurationCharacteristic.projectUuid) {
      return console.error('"activeConfigurationUuid" and "projectUuid" must be set!');
    }

    const {
      locationVisible,
      name,
      description,
      projectUuid,
      timezone,
    } = configurationCharacteristic;

    const areaInput = prepareAreaInput({
      rootAssetUuid,
      assets,
      assetsTreeRelations,
      assetsValues,
      settingsData,
      configurationCharacteristic,
    });

    // We don't use Promise.all() because cache update has to fire after all queries passed.
    if (!isCN) {
      await updateProject({
        variables: {
          private: !locationVisible,
          locationVisible,
          centreCoordinates,
          name,
          description,
          projectUuid,
        },
      });

      await setGlobalSimulationLocation({
        variables: {
          configUuid: activeConfigurationUuid,
          centreCoordinates,
          // TODO: Backend should refactor their code and remove boundaryId I think.
          boundaryId: JSON.stringify(centreCoordinates),
          visibility: true,
        },
      });
    }

    const settingsInput = prepareSettingsInput(settingsData);
    const { data: dataA, errors: errorsA } = await updateConfiguration({
      variables: {
        configurationUuid: activeConfigurationUuid,
        name,
        description,
        projectUuid,
        timezone,
        settingsData: settingsInput,
        scenarioData: {
          representation: areaInput,
        },
      },
      update(cache, result) {
        dispatch(setCommunityLogo(result?.data?.updateConfiguration?.settingsData?.logo ?? null));
        cacheUpdate.updateConfiguration(cache, result, {
          projectUuid,
          name: '',
          latitude: centreCoordinates.latitude,
          longitude: centreCoordinates.longitude,
          locationVisible: true,
        });
      },
    });
    if (errorsA) {
      const msg = (errorsA && errorsA[0]?.message) || 'Something went wrong';
      dispatch(
        openToast({
          message: msg,
          type: 'error',
        }),
      );
    } else if (dataA && dataA.updateConfiguration) {
      dispatch(
        openToast({
          message: 'Community updated successfully.',
          type: 'success',
        }),
      );
    }
  }, [
    dispatch,
    activeConfigurationUuid,
    configurationCharacteristic,
    rootAssetUuid,
    assets,
    assetsTreeRelations,
    assetsValues,
    settingsData,
    isCN,
    updateConfiguration,
    updateProject,
    centreCoordinates,
    setGlobalSimulationLocation,
  ]);

  const currentRefs = useCurrentRefs({
    isCommunityCreatedInDB,
    assetsAmountBelowCommunity,
    handleUpdateCommunity,
    zoomIntoConfiguration,
    dispatch,
  });

  const simulationStatus = useSelector(selectSimulationStatus);

  const addSaveKey = (key) => {
    saveKeyRef.current = key;
  };

  const saveWithoutDB = useCallback<
    (
      savingTreeBranch: TTreeBranch,
      connectorAssetUuid: string,
      gridMarketInTreeBranch: TAsset | undefined,
    ) => void
  >(
    (savingTreeBranch, connectorAssetUuid, gridMarketInTreeBranch) => {
      const areaResult = prepareAreaInput(
        gridMarketInTreeBranch
          ? {
              rootAssetUuid: savingTreeBranch.rootAssetUuid,
              assets: _.omit(savingTreeBranch.assets, [gridMarketInTreeBranch.uuid]),
              assetsTreeRelations: {
                ..._.omit(savingTreeBranch.assetsTreeRelations, [gridMarketInTreeBranch.uuid]),
                [savingTreeBranch.rootAssetUuid]: savingTreeBranch.assetsTreeRelations[
                  savingTreeBranch.rootAssetUuid
                ].filter((item) => item !== gridMarketInTreeBranch.uuid),
              },
              assetsValues: _.omit(savingTreeBranch.assetsValues, [gridMarketInTreeBranch.uuid]),
              settingsData,
              configurationCharacteristic,
            }
          : { ...savingTreeBranch, settingsData, configurationCharacteristic },
      );
      const loadingUuid =
        gridMarketInTreeBranch && areaResult ? areaResult.uuid : connectorAssetUuid;

      dispatch(setAssetLoadingInfo({ uuid: loadingUuid, isLoading: true }));
      setTimeout(() => dispatch(setAssetLoadingInfo({ uuid: loadingUuid, isLoading: false })), 100);
    },
    [configurationCharacteristic, settingsData, dispatch],
  );

  const saveNewAssetByMutation = useCallback<
    (
      areaResult: AreaInput,
      connectorAssetUuid: string,
      gridMarketInTreeBranch: TAsset | undefined,
    ) => Promise<boolean>
  >(
    (areaResult, connectorAssetUuid) => {
      //send request to BE
      return new Promise((resolve, reject) => {
        if (areaResult && activeConfigurationUuid) {
          if (saveKeyRef.current)
            dispatch(setAssetLoadingInfo({ uuid: saveKeyRef.current, isLoading: true }));
          return createConfigurationArea({
            variables: {
              areaRepresentation: { ...areaResult } as AreaInput,
              parentUuid: connectorAssetUuid,
              configurationUuid: activeConfigurationUuid,
            },
          })
            .then(async (data) => {
              if (data.errors && data.errors.length > 0) {
                const msg = data.errors[0].message;
                dispatch(
                  openToast({
                    message: msg || 'Something went wrong',
                    type: 'error',
                  }),
                );
                resolve(false);
              } else {
                resolve(true);
              }
              if (saveKeyRef.current)
                dispatch(setAssetLoadingInfo({ uuid: saveKeyRef.current, isLoading: false }));

              setTimeout(() => {
                if (data?.data?.createConfigurationArea && areaResult && areaResult.uuid)
                  dispatch(setLastAddedAssetUuid(areaResult.uuid));
              }, 2000);
            })
            .catch((e) => reject(e));
        }
        return resolve(false);
      });
    },
    [createConfigurationArea, activeConfigurationUuid, saveKeyRef, dispatch],
  );

  const handleAddNewAsset = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { values, chosenAsset, addUnderUuid, noStoreUpdate = false }: any,
      options?: { stayOnSetSelectedAssetUuid: boolean },
    ) => {
      const { libraryUUID } = values;
      const { stayOnSetSelectedAssetUuid } = options || {};
      const communityAssetUuid = communityAsset?.uuid || v4();
      const connectorAssetUuid = addUnderUuid || communityAssetUuid;

      const savingTreeBranch = createSavingTreeBranch(values, connectorAssetUuid, chosenAsset);

      const gridMarketInTreeBranch = Object.values(savingTreeBranch.assets).find((asset) =>
        GRID_MARKET_ASSET_TYPES.includes(asset.type),
      );

      const addingSingleDevice =
        savingTreeBranch.assets[savingTreeBranch.rootAssetUuid].type !== 'Area';

      if (isConfigEmpty) {
        // Add a top-level "Grid", a "Grid Market", a "Community" market and the currently saving asset.
        const gridAsset: TAssetArea = {
          type: 'Area',
          uuid: v4(),
          parentUuid: undefined,
        };
        const gridAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'Area',
            configType,
            values: { name: 'Grid Market' },
            advancedSettings: {},
          }),
        );

        const gridMarketAsset: TAsset = {
          type: 'InfiniteBus',
          uuid: v4(),
          parentUuid: gridAsset.uuid,
        };
        const gridMarketAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'InfiniteBus',
            configType,
            advancedSettings: {},
          }),
        );

        const communityAsset: TAsset = {
          type: 'Area',
          uuid: communityAssetUuid,
          parentUuid: gridAsset.uuid,
        };
        const communityAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'Area',
            configType,
            advancedSettings: {},
            values: { name: 'Community' },
          }),
        );

        if (!noStoreUpdate) {
          batch(() => {
            dispatch(setRootAsset(gridAsset));
            dispatch(addAssets([gridMarketAsset, communityAsset]));
            dispatch(
              updateAssetValuesForUuid([
                {
                  values: gridAssetValues,
                  uuid: gridAsset.uuid,
                },
                {
                  values: gridMarketAssetValues,
                  uuid: gridMarketAsset.uuid,
                },
                {
                  values: communityAssetValues,
                  uuid: communityAsset.uuid,
                },
              ]),
            );
            dispatch(appendBranch(savingTreeBranch));

            if (gridMarketInTreeBranch) {
              dispatch(removeAssetByUuid(gridMarketInTreeBranch.uuid));
            }

            if (!stayOnSetSelectedAssetUuid) {
              dispatch(
                setSelectedAssetUuid(
                  addingSingleDevice ? communityAsset.uuid : savingTreeBranch.rootAssetUuid,
                ),
              );
            }

            setDoZoomIntoConfiguration(true);
          });
        } else {
          batch(() => {
            if (!stayOnSetSelectedAssetUuid) {
              dispatch(
                setSelectedAssetUuid(
                  addingSingleDevice ? communityAsset?.uuid : savingTreeBranch.rootAssetUuid,
                ),
              );
            }

            dispatch(appendBranch(savingTreeBranch));
            if (gridMarketInTreeBranch) {
              dispatch(removeAssetByUuid(gridMarketInTreeBranch.uuid));
            }
          });
        }

        dispatch(updateSelectedLocation(null));
      } else {
        batch(() => {
          if (!stayOnSetSelectedAssetUuid) {
            dispatch(
              setSelectedAssetUuid(
                addingSingleDevice ? communityAsset?.uuid : savingTreeBranch.rootAssetUuid,
              ),
            );
          }
          dispatch(appendBranch(savingTreeBranch));
          if (gridMarketInTreeBranch) {
            dispatch(removeAssetByUuid(gridMarketInTreeBranch.uuid));
          }
        });
      }

      if (savingTreeBranch) {
        if (isCommunityCreatedInDB) {
          const areaInput = createAreaInputFromSavingTreeBranch(
            savingTreeBranch,
            gridMarketInTreeBranch,
          );
          if (isCN && simulationStatus === 'running') {
            // eslint-disable-next-line
            const getAreaType = (areaType: any): TAssetType => {
              return areaType;
            };

            const areaRep: TConfigTree = {
              type: getAreaType(areaInput?.type),
              uuid: areaInput?.uuid,
              children:
                areaInput?.children &&
                (areaInput?.children.map((item) => ({
                  type: item?.type || '',
                  uuid: item?.uuid || '',
                  children: [],
                })) as TConfigTree['children']),
            };
            if (isCN && areaInput) {
              syncConfigurationWithBE({
                actionType: 'create',
                configurationUuid: activeConfigurationUuid || '',
                parentUuid: connectorAssetUuid,
                configTree: areaRep,
                assetParams: areaInput,
                library: {
                  libraryUuid: libraryUUID || '',
                  rootAreaName: areaInput?.name || '',
                  geoTagLocation: selectedLocation
                    ? [selectedLocation?.lng, selectedLocation?.lat]
                    : areaInput?.geoTagLocation,
                },
              });
            }
          } else {
            if (areaInput) {
              saveNewAssetByMutation(areaInput, connectorAssetUuid, gridMarketInTreeBranch).then(
                (data) => {
                  if (!data) dispatch(removeAssetByUuidWithDelete(savingTreeBranch.rootAssetUuid));
                },
              );
            }
          }
          //disabled after configuration Area mutations
          //setDoSyncWithBackend(true);
        } else {
          // SaveWithout DB
          saveWithoutDB(savingTreeBranch, connectorAssetUuid, gridMarketInTreeBranch);
        }
      }

      return {
        addedAssetUuid: savingTreeBranch.rootAssetUuid,
      };
    },
    [
      communityAsset?.uuid,
      isConfigEmpty,
      configType,
      isCommunityCreatedInDB,
      createAreaInputFromSavingTreeBranch,
      saveNewAssetByMutation,
      dispatch,
      saveWithoutDB,
      createSavingTreeBranch,
      syncConfigurationWithBE,
      activeConfigurationUuid,
      isCN,
      simulationStatus,
      selectedLocation,
    ],
  );

  const handleAddAssetUnderUuid = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAddAssetUnderUuid']>>[0]) => {
      handleAddNewAsset(payload, { stayOnSetSelectedAssetUuid: true });
    },
    [handleAddNewAsset],
  );

  const handleAddNewCustomHouse = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAddNewCustomHouse']>>[0]) => {
      const { addedAssetUuid } = handleAddNewAsset(payload, { stayOnSetSelectedAssetUuid: true });

      // Important! setSelectedAssetUuid after setModalHouseSummaryView
      dispatch(setIsCustomHouseAddingContinuation(true));
      dispatch(setSelectedAssetUuid(addedAssetUuid));
    },
    [dispatch, handleAddNewAsset],
  );

  const handleAddNewCustomPV = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAddNewCustomPV']>>[0]) => {
      const { addedAssetUuid } = handleAddNewAsset(payload, { stayOnSetSelectedAssetUuid: true });
      dispatch(setSelectedAssetUuid(addedAssetUuid));
    },
    [dispatch, handleAddNewAsset],
  );

  const partialUpdateAssetByMutation = useCallback<(payload: TAssetsSaveProps) => Promise<boolean>>(
    (payload) => {
      return new Promise<boolean>((resolve) => {
        const areaParams: TDeviceTypesInput | undefined = createAreaInputFromSaveProps(payload);
        if (activeConfigurationUuid && areaParams) {
          if (saveKeyRef.current)
            dispatch(setAssetLoadingInfo({ uuid: saveKeyRef.current, isLoading: true }));
          return updateConfigurationArea({
            variables: {
              areaParams: areaParams,
              areaUuid: payload.assetUuid,
              configurationUuid: activeConfigurationUuid,
            },
          })
            .then((data) => {
              if (data.errors && data.errors.length > 0) {
                const parsedMessage = JSON.parse(data.errors[0].message);
                const msg = Object.values(parsedMessage).join('; ');
                dispatch(
                  openToast({
                    message: msg || 'Something went wrong',
                    type: 'error',
                  }),
                );
                resolve(false);
              }

              if (saveKeyRef.current)
                dispatch(setAssetLoadingInfo({ uuid: saveKeyRef.current, isLoading: false }));

              if (data.data?.updateConfigurationArea && payload && payload.assetUuid)
                dispatch(setLastAddedAssetUuid(payload.assetUuid));

              if (isCN && simulationStatus == 'running') {
                syncConfigurationWithBE({
                  actionType: 'singleUpdate',
                  configurationUuid: activeConfigurationUuid || '',
                  assetUuid: payload.assetUuid,
                  assetParams: {},
                });
              }
              resolve(true);
            })
            .catch(() => {
              if (saveKeyRef.current)
                dispatch(setAssetLoadingInfo({ uuid: saveKeyRef.current, isLoading: false }));
              resolve(false);
            });
        }
        resolve(false);
      });
    },
    [
      createAreaInputFromSaveProps,
      activeConfigurationUuid,
      dispatch,
      updateConfigurationArea,
      syncConfigurationWithBE,
      isCN,
      saveKeyRef,
      simulationStatus,
    ],
  );

  const dispatchUpdateAssetValues = useCallback<(payload: THandleAssetValuesParameters) => void>(
    (payload: THandleAssetValuesParameters) => {
      dispatch(updateAssetValuesForUuid({ uuid: payload.assetUuid, values: payload.values }));
      if (payload.assetType && payload.assetUuid) {
        dispatch(updateAssetTypeForUuid({ uuid: payload.assetUuid, assetType: payload.assetType }));
      }
    },
    [dispatch],
  );

  const handleAssetValuesSavePromise = useCallback<
    (payload: THandleAssetValuesParameters) => Promise<boolean>
  >(
    (payload) => {
      // https://gridsingularity.atlassian.net/browse/PH-1263
      // Grid Market Form in SCML: while selecting a home, ensure we have
      // coefficientPercentage value otherwise sum to 0 condition can be compromised
      const { values, assetUuid, assetType } = payload;
      const currentItem = scmMembers.find((item) => item.uuid === assetUuid);
      values['coefficientPercentage'] = currentItem?.coefficientPercentage;

      if (assetType === 'InfiniteBus') {
        // Grid Market From: Infinite bus (first option) in dropdown
        const rootUuid =
          Object.values(assets).find((item) => item.type === 'InfiniteBus')?.uuid || '';
        payload = {
          ...payload,
          assetUuid: rootUuid,
        };
        values['uuid'] = rootUuid;
      }

      dispatchUpdateAssetValues(payload);
      dispatch(setSelectedAssetUuid(undefined));

      return new Promise<boolean>(async (resolve) => {
        if (isCommunityCreatedInDB) {
          const result = await partialUpdateAssetByMutation(payload);
          resolve(result);
        } else {
          resolve(false);
        }
      });
    },
    [
      dispatchUpdateAssetValues,
      isCommunityCreatedInDB,
      partialUpdateAssetByMutation,
      scmMembers,
      assets,
      dispatch,
    ],
  );

  const handleAssetValuesSave = useCallback<(payload: THandleAssetValuesParameters) => void>(
    (payload) => {
      dispatchUpdateAssetValues(payload);

      if (isCommunityCreatedInDB) {
        partialUpdateAssetByMutation(payload);
      }
    },
    [dispatchUpdateAssetValues, isCommunityCreatedInDB, partialUpdateAssetByMutation],
  );

  const removeAssetByMutation = useCallback<(assetUuid: string) => void>(
    (assetUuid) => {
      if (activeConfigurationUuid && !isCN) {
        removeConfigurationArea({
          variables: {
            areaUuid: assetUuid,
            configurationUuid: activeConfigurationUuid,
          },
        }).then((data) => {
          if (data) dispatch(removeAssetByUuid(assetUuid));
          if (activeConfigurationUuid && isSCMFlow)
            handleReadConfiguration({
              variables: {
                uuid: activeConfigurationUuid,
              },
            });
        });
      }
      if (isCN) {
        syncConfigurationWithBE({
          actionType: 'delete',
          configurationUuid: activeConfigurationUuid || '',
          assetUuid,
        }).then((data) => {
          if (data) dispatch(removeAssetByUuid(assetUuid));
        });
      }
    },
    [
      activeConfigurationUuid,
      handleReadConfiguration,
      removeConfigurationArea,
      syncConfigurationWithBE,
      isCN,
      isSCMFlow,
      dispatch,
    ],
  );

  const handleAssetRemove = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAssetRemove']>>[0]) => {
      if (isCommunityCreatedInDB) {
        removeAssetByMutation(payload.assetUuid);
        //disabled after configuration Area mutations
        //setDoSyncWithBackend(true);
      }
    },
    [isCommunityCreatedInDB, removeAssetByMutation],
  );

  /*
   *
   *
   * Sync With Backend
   *
   */
  useEffect(
    function syncWithBackend() {
      (async () => {
        if (!doSyncWithBackend) return;

        if (!isLoggedIn) {
          if (
            currentRefs.current.assetsAmountBelowCommunity === 2 ||
            currentRefs.current.assetsAmountBelowCommunity === 50
          ) {
            currentRefs.current.dispatch(openModal(EPredefinedModalIds.MODAL_AUTH_REGISTER));
          }
          return;
        }

        if (!currentRefs.current.isCommunityCreatedInDB) {
          setDoSyncWithBackend(false);
        } else {
          await currentRefs.current.handleUpdateCommunity();
          setDoSyncWithBackend(false);
        }

        if (!isCN) {
          currentRefs.current.dispatch(resetSimulationResults());
        }
      })();
    },
    [assetsValues, assets, settingsData, isLoggedIn, doSyncWithBackend, currentRefs, isCN],
  );

  /*
   *
   *
   * Zoom into newly added asset (configuration)
   *
   */
  useEffect(() => {
    if (!doZoomIntoConfiguration) return;
    currentRefs.current.zoomIntoConfiguration();
    setDoZoomIntoConfiguration(false);
  }, [assetsValues, assets, settingsData, doZoomIntoConfiguration, currentRefs]);

  useEffect(() => {
    if (
      assetsAmountBelowCommunity === 2 &&
      !isCommunityCreatedInDB &&
      modalHouseSummaryView === initialApplicationState.modalHouseSummaryView
    ) {
      dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
    }
  }, [
    assetsAmountBelowCommunity,
    communityAsset?.uuid,
    dispatch,
    isCommunityCreatedInDB,
    isCustomHouseAddingContinuation,
    modalHouseSummaryView,
  ]);

  useEffect(() => {
    if (selectedLocation) {
      dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.SaveOrSettings));
    }
  }, [dispatch, selectedLocation]);

  return children({
    handleAddNewAsset,
    handleAddNewCustomHouse,
    handleAddNewCustomPV,
    handleAddAssetUnderUuid,
    handleAssetValuesSave,
    handleAssetRemove,
    handleAssetValuesSavePromise,
    addSaveKey,
  });
}
